import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createAction,
} from '@reduxjs/toolkit';
import { getTemperatureAndHumidity } from 'scenes/Renolog/services/getTemperatureAndHumidity.service';
import { getMouldRisk } from 'scenes/Renolog/services/getMouldRisk.service';
import { getStrength } from 'scenes/Renolog/services/getStrength.service';
import { getRenologDevices } from 'scenes/Renolog/services/getRenologDevices.service';
import { getCompanyDevices } from 'scenes/Renolog/services/getCompanyDevices.service';
import { getSensorOrders } from 'scenes/Renolog/services/getSensorOrders.service';
import { cancelSensorOrder as cancelSensorOrderService } from 'scenes/Renolog/services/cancelSensorOrder.service';

const deviceAdapter = createEntityAdapter({
  selectId: (entry) => entry.deviceId,
});

const initialProjectState = () => ({
  ...deviceAdapter.getInitialState(),
  sensorReadings: {},
  sensorOrders: [],
  companyDevices: [],
  loading: {
    devices: false,
    sensorData: {},
    temperatureHumidity: {},
    mouldRisk: {},
    strength: {},
    sensorOrders: false,
    companyDevices: false,
  },
  loadingStarted: {
    devices: false,
    sensorData: {},
    strength: {},
    sensorOrders: false,
    companyDevices: false,
  },
  error: {
    devices: undefined,
    sensorData: {},
    sensorOrders: undefined,
    companyDevices: undefined,
  },
});

const ensureProject = (state, projectId) => {
  if (!state.byProject[projectId]) {
    state.byProject[projectId] = initialProjectState();
  }
};

export const sensorDataCleared = createAction(
  'renologSensorData/sensorDataCleared',
  ({ projectId, deviceId }) => ({ payload: { projectId, deviceId } })
);

export const sensorDataBatchReceived = createAction(
  'renologSensorData/sensorDataBatchReceived',
  ({
    projectId,
    deviceId,
    temperatureHumidityBatch,
    mouldRiskBatch,
    strengthBatch,
  }) => ({
    payload: {
      projectId,
      deviceId,
      temperatureHumidityBatch,
      mouldRiskBatch,
      strengthBatch,
    },
  })
);

export const temperatureHumidityLoadingChanged = createAction(
  'renologSensorData/temperatureHumidityLoadingChanged',
  ({ projectId, deviceId, loading }) => ({
    payload: { projectId, deviceId, loading },
  })
);

export const mouldRiskLoadingChanged = createAction(
  'renologSensorData/mouldRiskLoadingChanged',
  ({ projectId, deviceId, loading }) => ({
    payload: { projectId, deviceId, loading },
  })
);

export const strengthLoadingChanged = createAction(
  'renologSensorData/strengthLoadingChanged',
  ({ projectId, deviceId, loading }) => ({
    payload: { projectId, deviceId, loading },
  })
);

const fetchAllPages = async (fetchFn, onBatch) => {
  let lastKey;
  do {
    const result = await fetchFn(lastKey);
    onBatch(result.items);
    lastKey = result.lastEvaluatedKey || undefined;
  } while (lastKey);
};

export const fetchSensorData = createAsyncThunk(
  'renolog/fetchSensorData',
  async ({ projectId, deviceId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const model = selectDeviceById(getState(), projectId, deviceId)?.model;

      dispatch(sensorDataCleared({ projectId, deviceId }));
      dispatch(
        temperatureHumidityLoadingChanged({
          projectId,
          deviceId,
          loading: true,
        })
      );

      const fetches = [
        fetchAllPages(
          (lastKey) => getTemperatureAndHumidity(projectId, deviceId, lastKey),
          (items) =>
            dispatch(
              sensorDataBatchReceived({
                projectId,
                deviceId,
                temperatureHumidityBatch: items,
              })
            )
        ).finally(() =>
          dispatch(
            temperatureHumidityLoadingChanged({
              projectId,
              deviceId,
              loading: false,
            })
          )
        ),
      ];

      if (model === 'BOX') {
        dispatch(
          mouldRiskLoadingChanged({ projectId, deviceId, loading: true })
        );
        fetches.push(
          fetchAllPages(
            (lastKey) => getMouldRisk(projectId, deviceId, lastKey),
            (items) =>
              dispatch(
                sensorDataBatchReceived({
                  projectId,
                  deviceId,
                  mouldRiskBatch: items,
                })
              )
          ).finally(() =>
            dispatch(
              mouldRiskLoadingChanged({ projectId, deviceId, loading: false })
            )
          )
        );
      }

      if (model === 'CURE') {
        dispatch(
          strengthLoadingChanged({ projectId, deviceId, loading: true })
        );
        fetches.push(
          fetchAllPages(
            (lastKey) => getStrength(projectId, deviceId, lastKey),
            (items) =>
              dispatch(
                sensorDataBatchReceived({
                  projectId,
                  deviceId,
                  strengthBatch: items,
                })
              )
          ).finally(() =>
            dispatch(
              strengthLoadingChanged({ projectId, deviceId, loading: false })
            )
          )
        );
      }

      await Promise.all(fetches);

      return { projectId, deviceId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch sensor data');
    }
  }
);

export const fetchStrengthData = createAsyncThunk(
  'renolog/fetchStrengthData',
  async ({ projectId, deviceId }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(strengthLoadingChanged({ projectId, deviceId, loading: true }));
      await fetchAllPages(
        (lastKey) => getStrength(projectId, deviceId, lastKey),
        (items) =>
          dispatch(
            sensorDataBatchReceived({
              projectId,
              deviceId,
              strengthBatch: items,
            })
          )
      ).finally(() =>
        dispatch(
          strengthLoadingChanged({ projectId, deviceId, loading: false })
        )
      );
      return { projectId, deviceId };
    } catch (error) {
      dispatch(strengthLoadingChanged({ projectId, deviceId, loading: false }));
      return rejectWithValue(error.message || 'Failed to fetch strength data');
    }
  }
);

export const fetchSensorOrders = createAsyncThunk(
  'renolog/fetchSensorOrders',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const orders = await getSensorOrders(projectId);
      return { orders, projectId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch sensor orders');
    }
  }
);

export const cancelSensorOrder = createAsyncThunk(
  'renolog/cancelSensorOrder',
  async ({ projectId, orderId }, { rejectWithValue }) => {
    try {
      await cancelSensorOrderService(projectId, orderId);
      return { projectId, orderId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to cancel sensor order');
    }
  }
);

export const fetchDevices = createAsyncThunk(
  'renolog/fetchDevices',
  async ({ projectId, doSync }, { rejectWithValue }) => {
    try {
      const devices = await getRenologDevices(projectId, doSync);
      return { devices, projectId, doSync };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch devices');
    }
  }
);

export const fetchCompanyDevices = createAsyncThunk(
  'renolog/fetchCompanyDevices',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const devices = await getCompanyDevices(projectId);
      return { devices, projectId };
    } catch (error) {
      return rejectWithValue(
        error.message || 'Failed to fetch company devices'
      );
    }
  }
);

const initialState = {
  byProject: {},
};

const sensorDataSlice = createSlice({
  name: 'renologSensorData',
  initialState,
  reducers: {
    sensorOrderAdded(state, action) {
      const { projectId, order } = action.payload;
      ensureProject(state, projectId);
      state.byProject[projectId].sensorOrders.push(order);
    },
    sensorOrderUpdated(state, action) {
      const { projectId, order } = action.payload;
      if (!state.byProject[projectId]) return;
      const index = state.byProject[projectId].sensorOrders.findIndex(
        (o) => o.id === order.id
      );
      if (index !== -1) {
        state.byProject[projectId].sensorOrders[index] = order;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sensorDataCleared, (state, action) => {
        const { projectId, deviceId } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].sensorReadings[deviceId] = {
          temperatureHumidity: [],
          mouldRisk: [],
          strength: [],
        };
      })
      .addCase(temperatureHumidityLoadingChanged, (state, action) => {
        const { projectId, deviceId, loading } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.temperatureHumidity[deviceId] =
          loading;
      })
      .addCase(mouldRiskLoadingChanged, (state, action) => {
        const { projectId, deviceId, loading } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.mouldRisk[deviceId] = loading;
      })
      .addCase(strengthLoadingChanged, (state, action) => {
        const { projectId, deviceId, loading } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.strength[deviceId] = loading;
      })
      .addCase(sensorDataBatchReceived, (state, action) => {
        const {
          projectId,
          deviceId,
          temperatureHumidityBatch,
          mouldRiskBatch,
          strengthBatch,
        } = action.payload;
        const project = state.byProject[projectId];
        if (!project) return;
        const readings = project.sensorReadings[deviceId];
        if (!readings) return;
        if (temperatureHumidityBatch) {
          readings.temperatureHumidity.push(...temperatureHumidityBatch);
        }
        if (mouldRiskBatch) {
          readings.mouldRisk.push(...mouldRiskBatch);
        }
        if (strengthBatch) {
          readings.strength.push(...strengthBatch);
        }
      })
      .addCase(fetchDevices.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.devices = true;
        state.byProject[projectId].loadingStarted.devices = true;
        state.byProject[projectId].error.devices = undefined;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        const { devices, projectId } = action.payload;
        ensureProject(state, projectId);
        deviceAdapter.setAll(state.byProject[projectId], devices);
        state.byProject[projectId].loading.devices = false;
        state.byProject[projectId].error.devices = undefined;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.devices = false;
        state.byProject[projectId].error.devices =
          action.payload || 'Unknown error';
      })
      .addCase(fetchSensorOrders.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.sensorOrders = true;
        state.byProject[projectId].loadingStarted.sensorOrders = true;
        state.byProject[projectId].error.sensorOrders = undefined;
      })
      .addCase(fetchSensorOrders.fulfilled, (state, action) => {
        const { orders, projectId } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].sensorOrders = orders;
        state.byProject[projectId].loading.sensorOrders = false;
        state.byProject[projectId].error.sensorOrders = undefined;
      })
      .addCase(fetchSensorOrders.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.sensorOrders = false;
        state.byProject[projectId].error.sensorOrders =
          action.payload || 'Unknown error';
      })
      .addCase(fetchSensorData.pending, (state, action) => {
        const { projectId, deviceId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.sensorData[deviceId] = true;
        state.byProject[projectId].loadingStarted.sensorData[deviceId] = true;
        state.byProject[projectId].error.sensorData[deviceId] = undefined;
      })
      .addCase(fetchSensorData.fulfilled, (state, action) => {
        const { projectId, deviceId } = action.payload;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.sensorData[deviceId] = false;
        state.byProject[projectId].error.sensorData[deviceId] = undefined;
      })
      .addCase(fetchSensorData.rejected, (state, action) => {
        const { projectId, deviceId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.sensorData[deviceId] = false;
        state.byProject[projectId].error.sensorData[deviceId] =
          action.payload || 'Unknown error';
      })
      .addCase(cancelSensorOrder.fulfilled, (state, action) => {
        const { projectId, orderId } = action.payload;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].sensorOrders = state.byProject[
          projectId
        ].sensorOrders.filter((o) => o.id !== orderId);
      })
      .addCase(fetchStrengthData.pending, (state, action) => {
        const { projectId, deviceId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loadingStarted.strength[deviceId] = true;
      })
      .addCase(fetchCompanyDevices.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.companyDevices = true;
        state.byProject[projectId].loadingStarted.companyDevices = true;
        state.byProject[projectId].error.companyDevices = undefined;
      })
      .addCase(fetchCompanyDevices.fulfilled, (state, action) => {
        const { devices, projectId } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].companyDevices = devices;
        state.byProject[projectId].loading.companyDevices = false;
        state.byProject[projectId].error.companyDevices = undefined;
      })
      .addCase(fetchCompanyDevices.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.companyDevices = false;
        state.byProject[projectId].error.companyDevices =
          action.payload || 'Unknown error';
      });
  },
});

export const { sensorOrderAdded, sensorOrderUpdated } = sensorDataSlice.actions;

export const selectDevicesForProject = (state, projectId) =>
  Object.values(state.renologSensorData.byProject[projectId]?.entities || {});

export const selectDeviceById = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.entities?.[deviceId];

export const selectSensorReadings = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.sensorReadings?.[deviceId];

export const selectDeviceLoading = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loading?.devices ?? false;

export const selectDevicesLoadingStarted = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.devices ??
  false;

export const selectSensorDataLoading = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.loading?.sensorData?.[
    deviceId
  ] ?? false;

export const selectTemperatureHumidityLoading = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.loading?.temperatureHumidity?.[
    deviceId
  ] ?? false;

export const selectMouldRiskLoading = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.loading?.mouldRisk?.[
    deviceId
  ] ?? false;

export const selectStrengthLoading = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.loading?.strength?.[deviceId] ??
  false;

export const selectStrengthLoadingStarted = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.strength?.[
    deviceId
  ] ?? false;

export const selectSensorDataLoadingStarted = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.sensorData?.[
    deviceId
  ] ?? false;

export const selectSensorDataLoadingStartedForProject = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.sensorData ||
  {};

export const selectSensorOrders = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.sensorOrders || [];

export const selectSensorOrdersLoadingStarted = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.sensorOrders ??
  false;

export const selectCompanyDevices = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.companyDevices || [];

export const selectCompanyDevicesLoadingStarted = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted
    ?.companyDevices ?? false;

export const selectCompanyDevicesLoading = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loading?.companyDevices ??
  false;

export const selectDeviceError = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.error?.devices;

export const selectSensorDataError = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.error?.sensorData?.[deviceId];

export default sensorDataSlice.reducer;
